import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Undo2, ShieldCheck, ShieldOff, Mail } from "lucide-react";
import InvitePanel from "@/components/InvitePanel";
import DeletionRequests from "@/components/DeletionRequests";
import AlertSettings from "@/components/AlertSettings";
import ActivityLog from "@/components/ActivityLog";

function UserRow({ u, me, onStatus, onRole }) {
  const isSelf = u.id === me.id;
  const active = u.status === "approved";
  return (
    <div data-testid={`leader-row-${u.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate" data-testid={`leader-name-${u.id}`}>{u.name} {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}</div>
        <div className="text-xs text-muted-foreground truncate">{u.email} · <span data-testid={`leader-role-${u.id}`} className="capitalize">{u.role}</span></div>
      </div>
      <Badge variant={active ? "default" : "destructive"} data-testid={`leader-status-${u.id}`} className="w-fit">{active ? "Active" : "Disabled"}</Badge>
      {!isSelf && (
        <div className="flex flex-wrap gap-2">
          {active && (u.role === "admin" ? (
            <Button size="sm" variant="outline" data-testid={`demote-${u.id}`} onClick={() => onRole(u.id, "standard")}><ShieldOff className="w-4 h-4 mr-1" /> Make standard</Button>
          ) : (
            <Button size="sm" variant="outline" data-testid={`promote-${u.id}`} onClick={() => onRole(u.id, "admin")}><ShieldCheck className="w-4 h-4 mr-1" /> Make admin</Button>
          ))}
          {active ? (
            <Button size="sm" variant="destructive" data-testid={`reject-${u.id}`} onClick={() => onStatus(u.id, "rejected")}><X className="w-4 h-4 mr-1" /> Disable</Button>
          ) : (
            <Button size="sm" data-testid={`approve-${u.id}`} onClick={() => onStatus(u.id, "approved")}><Undo2 className="w-4 h-4 mr-1" /> Re-enable</Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Leaders() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, i, r] = await Promise.all([api.get("/admin/users"), api.get("/admin/invitations"), api.get("/admin/deletion-requests")]);
      setUsers(u.data); setInvites(i.data); setRequests(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchUser = (data) => setUsers((prev) => prev.map((u) => (u.id === data.id ? data : u)));
  const onStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/admin/users/${id}/status`, { status });
      patchUser(data);
      toast.success(`${data.name} ${status === "approved" ? "re-enabled" : "disabled"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const onRole = async (id, role) => {
    try {
      const { data } = await api.put(`/admin/users/${id}/role`, { role });
      patchUser(data);
      toast.success(`${data.name} is now ${role === "admin" ? "an admin" : "a standard user"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendDigest = async () => {
    setSending(true);
    try { await api.post("/admin/digest/send-now"); toast.success("Weekly digest is on its way to all admins"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSending(false);
  };

  return (
    <div className="space-y-6" data-testid="leaders-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Users & approvals</h1>
          <p className="text-sm text-muted-foreground">Invite leaders, set access levels and approve deletions.</p>
        </div>
        <Button variant="outline" data-testid="send-digest-btn" onClick={sendDigest} disabled={sending} className="min-h-[44px]">
          <Mail className="w-4 h-4 mr-1.5" /> {sending ? "Sending…" : "Send weekly digest now"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="digest-schedule-note">
        A digest of attendance, overdue fees and absence alerts is emailed every Friday at 9:00 (Dublin time) to admins who have the "Friday digest email" switch turned on (see the sidebar).
      </p>
      <DeletionRequests requests={requests} onChanged={load} />
      <InvitePanel invites={invites} onChanged={load} />
      <AlertSettings />
      <Card className="card-shadow-soft">
        <CardHeader><CardTitle className="text-lg">All users</CardTitle></CardHeader>
        <CardContent>
          {users.map((u) => <UserRow key={u.id} u={u} me={user} onStatus={onStatus} onRole={onRole} />)}
        </CardContent>
      </Card>
      <ActivityLog />
    </div>
  );
}
